/**
 * Client Quick Card
 * Mobile-first client overview with health, financials, activity.
 */

import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Building2, Mail, Phone, Hash, Globe, Link2, FileText, MessageSquare, Calendar, TrendingUp, TrendingDown, Minus, Mic } from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/ma-client';

const fmtCurrency = (v: number) => `$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtBytes = (b: number) => b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;

const HEALTH_COLORS: Record<string, string> = {
  excellent: 'text-green-600 dark:text-green-400 border-green-500/30',
  good: 'text-blue-600 dark:text-blue-400 border-blue-500/30',
  fair: 'text-amber-600 dark:text-amber-400 border-amber-500/30',
  poor: 'text-red-600 dark:text-red-400 border-red-500/30',
};

export default function ClientCardPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['client-card', clientId],
    queryFn: async () => { const r = await apiClient.get(`/manager/client/${clientId}`); return r.data.data; },
    enabled: !!clientId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--v4-accent)]" />
      </div>
    );
  }

  if (!data) return null;

  const { client, health, stats, financials, recentChats, recentDocs } = data;
  const healthColor = HEALTH_COLORS[health.level] || HEALTH_COLORS.fair;

  return (
    <div className="px-5 py-4 max-w-lg mx-auto space-y-5 pb-24">
      {/* Back button */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Client Header */}
      <div className="flex items-start gap-4">
        <div className={cn('w-14 h-14 rounded-xl border-2 flex items-center justify-center shrink-0', healthColor)}>
          <span className="text-xl font-bold">{health.score}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold truncate">{client.name}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {client.industry && <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">{client.industry}</span>}
            {client.xeroConnected && <span className="text-xs text-green-600 dark:text-green-400 bg-green-500/10 px-2 py-0.5 rounded">Xero</span>}
            <span className={cn('text-xs px-2 py-0.5 rounded capitalize', health.level === 'excellent' ? 'text-green-600 dark:text-green-400 bg-green-500/10' : health.level === 'good' ? 'text-blue-600 dark:text-blue-400 bg-blue-500/10' : health.level === 'fair' ? 'text-amber-600 dark:text-amber-400 bg-amber-500/10' : 'text-red-600 dark:text-red-400 bg-red-500/10')}>{health.level}</span>
          </div>
        </div>
      </div>

      {/* Quick Action: Ask about this client */}
      <button
        onClick={() => navigate(`/pa/voice`)}
        className="w-full flex items-center gap-3 p-4 bg-[var(--v4-accent)]/10 border border-[var(--v4-accent)]/20 rounded-xl transition hover:bg-[var(--v4-accent)]/15"
      >
        <Mic className="h-5 w-5 text-[var(--v4-accent)]" />
        <span className="text-sm">Ask about {client.name.split(' ')[0]}</span>
      </button>

      {/* Contact Info */}
      <div className="space-y-2">
        {client.abn && (
          <div className="flex items-center gap-3 text-sm">
            <Hash className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">ABN: {client.abn}</span>
          </div>
        )}
        {client.email && (
          <div className="flex items-center gap-3 text-sm">
            <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
            <a href={`mailto:${client.email}`} className="text-[var(--v4-accent)] hover:underline truncate">{client.email}</a>
          </div>
        )}
        {client.phone && (
          <div className="flex items-center gap-3 text-sm">
            <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
            <a href={`tel:${client.phone}`} className="text-muted-foreground">{client.phone}</a>
          </div>
        )}
        {client.xeroOrgName && (
          <div className="flex items-center gap-3 text-sm">
            <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground truncate">{client.xeroOrgName}</span>
          </div>
        )}
      </div>

      {/* Financial Snapshot */}
      {financials && (
        <div className="bg-card border border-border rounded-xl p-4 space-y-3">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{financials.period} Financials</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Revenue</p>
              <p className="text-lg font-bold text-green-600 dark:text-green-400">{fmtCurrency(financials.revenue)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Expenses</p>
              <p className="text-lg font-bold text-red-600 dark:text-red-400">{fmtCurrency(financials.expenses)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Net Profit</p>
              <p className={cn('text-lg font-bold', financials.netProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>
                {financials.netProfit >= 0 ? '' : '-'}{fmtCurrency(financials.netProfit)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Profit Margin</p>
              <div className="flex items-center gap-1">
                {financials.profitMargin > 0 ? <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" /> : financials.profitMargin < 0 ? <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-400" /> : <Minus className="h-4 w-4 text-muted-foreground" />}
                <p className={cn('text-lg font-bold', financials.profitMargin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}>{financials.profitMargin}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-card border border-border rounded-xl p-3 text-center">
          <FileText className="h-4 w-4 text-[var(--v4-accent)] mx-auto mb-1" />
          <p className="text-xl font-bold">{stats.documents}</p>
          <p className="text-xs text-muted-foreground">Docs</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-3 text-center">
          <MessageSquare className="h-4 w-4 text-[var(--v4-accent)] mx-auto mb-1" />
          <p className="text-xl font-bold">{stats.conversations}</p>
          <p className="text-xs text-muted-foreground">Chats</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-3 text-center">
          <Calendar className="h-4 w-4 text-amber-600 dark:text-amber-400 mx-auto mb-1" />
          <p className="text-xl font-bold">{stats.daysSinceContact ?? '-'}</p>
          <p className="text-xs text-muted-foreground">Days ago</p>
        </div>
      </div>

      {/* Health Factors */}
      <div className="space-y-2">
        <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Health Breakdown</h3>
        {health.factors.map((f: any) => (
          <div key={f.name} className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground w-20">{f.name}</span>
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all', f.score / f.max >= 0.7 ? 'bg-green-500' : f.score / f.max >= 0.4 ? 'bg-amber-500' : 'bg-red-500')}
                style={{ width: `${(f.score / f.max) * 100}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground w-10 text-right">{f.score}/{f.max}</span>
          </div>
        ))}
      </div>

      {/* Recent Chats */}
      {recentChats.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Recent Conversations</h3>
          {recentChats.map((c: any) => (
            <div key={c.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
              <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
              <p className="text-sm text-muted-foreground flex-1 truncate">{c.title || 'Chat'}</p>
              <span className="text-xs text-muted-foreground shrink-0">{new Date(c.updated_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
            </div>
          ))}
        </div>
      )}

      {/* Recent Documents */}
      {recentDocs.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Recent Documents</h3>
          {recentDocs.map((d: any) => (
            <div key={d.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground truncate">{d.original_filename}</p>
                {d.document_category && <span className="text-xs text-muted-foreground">{d.document_category}</span>}
              </div>
              <span className="text-xs text-muted-foreground shrink-0">{new Date(d.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
